import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { client } from "@/lib/apollo-client";
import { ADMIN_BRAND_LEAD_SUBMISSIONS } from "@/queries/brand-lead-submissions";
import type { AdminBrandLeadSubmissionsQuery } from "@/generated/brand-lead-submissions";
import * as XLSX from "xlsx";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, ChevronLeft, ChevronRight, Download, Inbox } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import {
  describeLeadOrigin,
  describeLeadUtm,
  LEAD_ORIGIN_GROUPS,
  type LeadOriginGroup,
} from "@/lib/brandLeads/leadOrigin";
import { cn } from "@/lib/utils";

type AdminBrandLeadSubmissionsResponse = AdminBrandLeadSubmissionsQuery["adminBrandLeadSubmissions"];
type BrandLeadSubmission = AdminBrandLeadSubmissionsResponse["data"][number];

const BUDGET_LABEL: Record<string, string> = {
  range_1k_5k: "R$ 1.000 – 5.000",
  range_5k_10k: "R$ 5.000 – 10.000",
  range_10k_50k: "R$ 10.000 – 50.000",
  range_50k_100k: "R$ 50.000 – 100.000",
  range_100k_plus: "R$ 100.000+",
  undecided: "Ainda definindo",
  range_5k_20k: "R$ 5 mil – 20 mil",
  range_10k_20k: "R$ 10 mil – 20 mil",
  range_20k_50k: "R$ 20 mil – 50 mil",
  range_50k_150k: "R$ 50 mil – 150 mil",
  range_150k_plus: "Acima de R$ 150 mil",
};

const ORIGIN_BADGE_CLASS: Record<LeadOriginGroup, string> = {
  app: "border-primary/40 bg-primary/10 text-primary",
  site: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  marcas: "border-violet-500/40 bg-violet-500/10 text-violet-300",
  other: "text-muted-foreground",
};

const LeadOriginCell = ({ source, utm }: { source?: string | null; utm?: unknown }) => {
  const origin = describeLeadOrigin(source);
  const campaign = describeLeadUtm(utm);
  if (!origin.label && !campaign) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col items-start gap-1">
      {origin.label && (
        <Badge variant="outline" className={cn("whitespace-nowrap font-normal", ORIGIN_BADGE_CLASS[origin.group])}>
          {origin.label}
        </Badge>
      )}
      {campaign && <span className="text-xs text-muted-foreground">{campaign}</span>}
    </div>
  );
};

async function fetchBrandLeadSubmissions(variables: {
  page: number;
  pageSize: number;
  search?: string;
  origin?: LeadOriginGroup;
}): Promise<AdminBrandLeadSubmissionsResponse> {
  const { data } = await client.query<AdminBrandLeadSubmissionsQuery>({
    query: ADMIN_BRAND_LEAD_SUBMISSIONS,
    variables,
    fetchPolicy: "no-cache",
    errorPolicy: "none",
  });
  if (!data?.adminBrandLeadSubmissions) throw new Error("Failed to load contact requests");
  return data.adminBrandLeadSubmissions;
}

const PAGE_SIZE = 25;

const AdminContatos = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [origin, setOrigin] = useState<LeadOriginGroup | null>(null);
  const [data, setData] = useState<AdminBrandLeadSubmissionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchBrandLeadSubmissions({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch || undefined,
      origin: origin ?? undefined,
    })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Erro inesperado");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, origin]);

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageCount = data?.pageCount ?? 1;
  const originCounts = data?.originCounts;
  const allOriginsCount = originCounts
    ? LEAD_ORIGIN_GROUPS.reduce((sum, group) => sum + originCounts[group], 0)
    : null;
  // "Outras" only appears when some request actually has an unknown source.
  const originChips = LEAD_ORIGIN_GROUPS.filter(
    (group) => group !== "other" || (originCounts?.other ?? 0) > 0 || origin === "other",
  );

  const selectOrigin = (next: LeadOriginGroup | null) => {
    setOrigin(next);
    setPage(1);
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const all: BrandLeadSubmission[] = [];
      const exportPageSize = 100;
      let currentPage = 1;
      let pages = 1;
      do {
        const res = await fetchBrandLeadSubmissions({
          page: currentPage,
          pageSize: exportPageSize,
          search: debouncedSearch || undefined,
          origin: origin ?? undefined,
        });
        all.push(...res.data);
        pages = res.pageCount;
        currentPage += 1;
      } while (currentPage <= pages);

      const sheetData = all.map(({ lead, isDuplicate, submittedAt }) => ({
        Nome: lead.name,
        Email: lead.email,
        Telefone: lead.phone,
        Site: lead.website ?? "",
        Empresa: lead.company ?? "",
        Perfil: lead.demandType ?? "",
        Objetivo: lead.objective ?? "",
        Conteúdo: Array.isArray(lead.contentType) ? lead.contentType.join(", ") : "",
        Plataformas: lead.platforms ?? "",
        "Redes sociais": lead.social ?? "",
        Orçamento: BUDGET_LABEL[lead.budget] ?? lead.budget,
        Origem: describeLeadOrigin(lead.source).label,
        "Campanha (UTM)": describeLeadUtm(lead.utm) ?? "",
        [t("admin.contacts.requestType")]: t(isDuplicate ? "admin.contacts.duplicate" : "admin.contacts.original"),
        "Recebido em": submittedAt,
        "Criado em": lead.createdAt,
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Contatos");
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `contatos-${stamp}.xlsx`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao exportar");
    } finally {
      setExporting(false);
    }
  };

  const showingRange = useMemo(() => {
    if (total === 0) return "0";
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);
    return `${start}–${end}`;
  }, [page, total]);

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Contatos</h1>
            <p className="text-sm text-muted-foreground">
              {t("admin.contacts.description")}
            </p>
          </div>
          <Button onClick={exportXlsx} disabled={exporting || total === 0} className="gap-2">
            <Download className="h-4 w-4" />
            {exporting ? "Exportando…" : "Exportar XLSX"}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2">
              <span>
                {t("admin.contacts.requestCount", { count: total })}
              </span>
              <div className="relative w-full max-w-xs">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nome, email, telefone ou site"
                  className="pl-9"
                />
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label={t("admin.contacts.origins.label")}>
              {[null, ...originChips].map((group) => {
                const selected = origin === group;
                const count = group === null ? allOriginsCount : originCounts?.[group];
                return (
                  <Button
                    key={group ?? "all"}
                    type="button"
                    size="sm"
                    variant={selected ? "default" : "outline"}
                    aria-pressed={selected}
                    onClick={() => selectOrigin(group)}
                    className="gap-2"
                  >
                    {t(`admin.contacts.origins.${group ?? "all"}`)}
                    {count != null && (
                      <span className="rounded-full bg-background/20 px-1.5 text-xs tabular-nums">{count}</span>
                    )}
                  </Button>
                );
              })}
            </div>

            {error && (
              <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Orçamento</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Recebido em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                        Carregando…
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                        <div className="flex flex-col items-center gap-2">
                          <Inbox className="h-8 w-8 opacity-40" />
                          Nenhum contato ainda.
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(({ documentId, lead, isDuplicate, submittedAt }) => (
                      <TableRow key={documentId}>
                        <TableCell className="font-medium">
                          <span>{lead.name}</span>
                          {isDuplicate && (
                            <Badge variant="secondary" className="ml-2" title={t("admin.contacts.duplicateHint")}>
                              {t("admin.contacts.duplicate")}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <a className="hover:underline" href={`mailto:${lead.email}`}>
                            {lead.email}
                          </a>
                        </TableCell>
                        <TableCell>
                          <a
                            className="hover:underline"
                            href={`https://wa.me/${lead.phone.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {lead.phone}
                          </a>
                        </TableCell>
                        <TableCell className="max-w-[180px] truncate">
                          {lead.company || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="max-w-[160px] truncate">
                          {lead.demandType || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>{BUDGET_LABEL[lead.budget] ?? lead.budget}</TableCell>
                        <TableCell>
                          <LeadOriginCell source={lead.source} utm={lead.utm} />
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDateTime(submittedAt)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {pageCount > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Mostrando {showingRange} de {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Página {page} / {pageCount}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={page >= pageCount || loading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminContatos;
